import React, {useState, useCallback, useRef, useEffect} from "react";

/* A coverflow "wheel": the focused card sits upright and forward, its
   neighbours rotate away in 3D so the row reads as an arc. Cards more than
   VISIBLE_SPAN steps from the centre are dropped from the DOM flow so a long
   row stays cheap to render. */

const VISIBLE_SPAN = 3;

export default function ConfigWheel({title, count, items, onOpen}) {
  const [index, setIndex] = useState(0);
  const trackRef = useRef(null);
  const total = items.length;

  const step = useCallback(
    delta => {
      setIndex(prev => {
        if (total === 0) return 0;
        return (prev + delta + total) % total;
      });
    },
    [total]
  );

  // Arrow keys drive the row once it has focus.
  useEffect(() => {
    const node = trackRef.current;
    if (!node) return undefined;
    const onKey = event => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        step(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        step(1);
      }
    };
    node.addEventListener("keydown", onKey);
    return () => node.removeEventListener("keydown", onKey);
  }, [step]);

  if (total === 0) {
    return null;
  }

  /* Shortest signed distance from the focused card, so the row wraps around
     instead of dead-ending at either edge. */
  const offsetOf = i => {
    let offset = i - index;
    if (offset > total / 2) offset -= total;
    if (offset < -total / 2) offset += total;
    return offset;
  };

  return (
    <section className="cfg-row">
      <div className="cfg-row-head">
        <h2 className="cfg-row-title">{title}</h2>
        <span className="cfg-row-count">{count}</span>
      </div>

      <div className="cfg-wheel">
        <button
          type="button"
          className="cfg-arrow cfg-arrow-prev"
          onClick={() => step(-1)}
          aria-label={`Previous ${title}`}
          disabled={total < 2}
        >
          <i className="fas fa-arrow-left"></i>
        </button>

        <div
          className="cfg-track"
          ref={trackRef}
          tabIndex={0}
          role="group"
          aria-label={`${title} carousel`}
        >
          {items.map((item, i) => {
            const offset = offsetOf(i);
            const distance = Math.abs(offset);
            if (distance > VISIBLE_SPAN) return null;

            const isCenter = offset === 0;
            const direction = Math.sign(offset);
            const translate = offset * 46;
            const rotate = -direction * Math.min(distance, 2) * 22;
            const scale = Math.max(1 - distance * 0.14, 0.55);
            const depth = -distance * 120;

            return (
              <figure
                key={item.id}
                className={`cfg-card${isCenter ? " cfg-card-active" : ""}`}
                style={{
                  transform: `translateX(${translate}%) translateZ(${depth}px) rotateY(${rotate}deg) scale(${scale})`,
                  zIndex: VISIBLE_SPAN - distance,
                  opacity: distance > 2 ? 0 : 1,
                  pointerEvents: distance > 2 ? "none" : "auto"
                }}
                onClick={() => (isCenter ? onOpen(item) : setIndex(i))}
              >
                <div className="cfg-card-media">
                  <img src={item.src} alt={item.label} loading="lazy" />
                </div>
                <figcaption className="cfg-card-label">{item.label}</figcaption>
              </figure>
            );
          })}
        </div>

        <button
          type="button"
          className="cfg-arrow cfg-arrow-next"
          onClick={() => step(1)}
          aria-label={`Next ${title}`}
          disabled={total < 2}
        >
          <i className="fas fa-arrow-right"></i>
        </button>
      </div>

      <div className="cfg-dots">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            className={`cfg-dot${i === index ? " cfg-dot-active" : ""}`}
            onClick={() => setIndex(i)}
            aria-label={item.label}
          />
        ))}
      </div>
    </section>
  );
}
